import { ReasoningStep, SuggestedQuestion } from './actions';
import { CHAT_CONFIG, clearMemoryService, downloadAskAicaFileService, getChatHistoryService, sendChatRequest } from './service';

interface Message {
    role: string;
    content: string;
}

interface StreamChunk {
    delta: {
        role: string;
        content: string;
    };
    message: {
        role: string;
        content: string;
    };
    is_final: boolean;
    suggested_questions?: SuggestedQuestion[];
    has_suggested_questions?: boolean;
    reasoning?: ReasoningStep[] | null;
    has_new_reasoning?: boolean;
    metadata: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
    };
}

export interface AskAicaResponse {
    content: string;
    sources?: string[];
    suggestedQuestions?: SuggestedQuestion[];
    hasNewSuggestedQuestions?: boolean;
    reasoning?: ReasoningStep[] | null;
    hasNewReasoning?: boolean;
    metadata?: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
    };
}

/** Mutable state tracked across SSE chunks for a single streaming request. */
interface StreamState {
    isFirstChunk: boolean;
    suggestedQuestions: SuggestedQuestion[] | undefined;
    reasoning: ReasoningStep[] | null;
    hasNewReasoning: boolean;
    metadata: StreamChunk['metadata'] | undefined;
    lastServerContent: string;
}

/** Parse SSE-formatted lines and invoke `onParsed` for each valid data frame. */
const parseSSELines = (lines: string[], onParsed: (data: StreamChunk) => void) => {
    for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        if (line.includes('[DONE]')) continue;

        try {
            const jsonData = line.slice(5).trim();
            if (jsonData) {
                onParsed(JSON.parse(jsonData));
            }
        } catch (error) {
            console.error('Error parsing SSE data:', error, line);
        }
    }
};

/**
 * Process a single parsed SSE chunk — updates tracking state and calls onChunk.
 * Uses server-accumulated `message.content` as the source of truth so the
 * reducer can replace (not append) and self-heal if any earlier chunk was lost.
 */
const processStreamChunk = (
    parsedData: StreamChunk,
    state: StreamState,
    onChunk: (
        chunk: string,
        suggestedQuestions?: SuggestedQuestion[],
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        isFirst?: boolean,
        metadata?: any,
    ) => void,
) => {
    if (parsedData.message && parsedData.message.content) {
        state.lastServerContent = parsedData.message.content;
    }

    if (parsedData.suggested_questions && parsedData.has_suggested_questions) {
        state.suggestedQuestions = parsedData.suggested_questions;
    }

    state.reasoning = parsedData.reasoning ?? null;
    state.hasNewReasoning = parsedData.has_new_reasoning ?? false;

    if (state.metadata === undefined) {
        state.metadata = parsedData.metadata;
    }

    // Check for error in reasoning steps
    let isError = false;
    let errorMessage = '';
    if (Array.isArray(parsedData.reasoning)) {
        const errorReason = parsedData.reasoning.find((step) => step.type === 'error');
        if (errorReason) {
            isError = true;
            errorMessage = errorReason.message;
        }
    }

    const chunkMetadata = {
        ...parsedData.metadata,
        ...(isError && { error: true, errorMessage }),
    };

    const currentIsFirst = state.isFirstChunk;
    if (state.isFirstChunk) state.isFirstChunk = false;

    onChunk(
        state.lastServerContent,
        parsedData.suggested_questions,
        parsedData.reasoning,
        state.hasNewReasoning,
        currentIsFirst,
        chunkMetadata,
    );
};

/**
 * Helper function to send messages to the Ask Aica API with streaming enabled
 *
 * This implementation uses XMLHttpRequest for better control over the streaming request
 * to avoid any browser quirks with fetch API and SSE implementation
 *
 * @param messages Array of messages in the conversation
 * @param onChunk Callback that receives each chunk of the stream
 * @param onComplete Callback that is called when the stream is complete
 * @param onError Callback for error handling
 * @returns A function to abort the stream
 */
export const streamAskAicaChat = (
    messages: Message[],
    sources: string[],
    user_id: string,
    org_id: string,
    onChunk: (
        chunk: string,
        suggestedQuestions?: SuggestedQuestion[],
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        isFirst?: boolean,
        metadata?: any,
    ) => void,
    onComplete: (response: AskAicaResponse) => void,
    onError: (error: any) => void,
    flowKey?: string,
): (() => void) => {
    const xhr = new XMLHttpRequest();

    const state: StreamState = {
        isFirstChunk: true,
        suggestedQuestions: undefined,
        reasoning: null,
        hasNewReasoning: false,
        metadata: undefined,
        lastServerContent: '',
    };
    let processedLength = 0; // How much of xhr.responseText we have consumed
    let lineBuffer = ''; // Carries partial SSE lines across XHR progress events

    const handleParsedChunk = (parsedData: StreamChunk) => processStreamChunk(parsedData, state, onChunk);

    xhr.open('POST', CHAT_CONFIG.URL);

    const headers = CHAT_CONFIG.getHeaders(org_id, user_id);
    Object.keys(headers).forEach((key) => {
        xhr.setRequestHeader(key, headers[key]);
    });

    xhr.onreadystatechange = function () {
        if (xhr.readyState === 3 || xhr.readyState === 4) {
            // Extract only the new bytes since our last read
            const newData = xhr.responseText.substring(processedLength);
            processedLength = xhr.responseText.length;

            if (newData) {
                // Prepend any leftover partial line from the previous event
                const combined = lineBuffer + newData;
                const lines = combined.split('\n');

                // The last element may be an incomplete line (no trailing \n).
                // Hold it back until the next event completes it.
                lineBuffer = lines.pop() || '';

                parseSSELines(lines, handleParsedChunk);
            }
        }

        if (xhr.readyState === 4) {
            // Flush any remaining data in the line buffer
            if (lineBuffer.trim()) {
                parseSSELines([lineBuffer], handleParsedChunk);
                lineBuffer = '';
            }

            if (xhr.status >= 200 && xhr.status < 300) {
                onComplete({
                    content: state.lastServerContent,
                    suggestedQuestions: state.suggestedQuestions,
                    hasNewSuggestedQuestions: !!state.suggestedQuestions,
                    reasoning: state.reasoning,
                    hasNewReasoning: state.hasNewReasoning,
                    metadata: state.metadata,
                });
            } else {
                onError(new Error(`HTTP error! Status: ${xhr.status}`));
            }
        }
    };

    xhr.onerror = function () {
        onError(new Error('Network error occurred'));
    };

    xhr.send(
        JSON.stringify({
            user_id,
            org_id,
            sources,
            messages,
            stream: true,
            flow_key: flowKey ?? 'user_chat',
        }),
    );

    return () => {
        xhr.abort();
    };
};

/**
 * Helper function to send messages to the Ask Aica API without streaming
 * @param messages Array of messages in the conversation
 * @returns Promise with the response
 */
export const askAicaChat = async (messages: Message[]): Promise<AskAicaResponse> => {
    try {
        const response = await sendChatRequest({ messages });
        return {
            content: response.data.content,
            metadata: response.data.metadata,
        };
    } catch (error) {
        console.error('Error in askAicaChat:', error);
        throw error;
    }
};

export const getChatHistory = async (userId, orgId, pageNum, pageSize) => {
    try {
        const response = await getChatHistoryService({ userId, orgId, pageNum, pageSize });
        return response.data;
    } catch (err) {
        console.error('Error in getChatHistory:', err);
        throw err;
    }
};

/**
 * Download a generated file from Athena and trigger a browser save dialog.
 * Auth + URL construction are handled by the service layer (axios client).
 * `orgId` is the context org the chat ran under (see downloadAskAicaFileService).
 * Throws on failure so the caller can surface a user-facing error.
 */
export const downloadAskAicaFile = async (fileUrl: string, fileName: string, orgId?: string): Promise<void> => {
    const response = await downloadAskAicaFileService(fileUrl, orgId);
    const objectUrl = window.URL.createObjectURL(response.data);
    try {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        window.URL.revokeObjectURL(objectUrl);
    }
};

export const clearMemory = async (userId: string, orgId: string) => {
    try {
        const response = await clearMemoryService({ userId, orgId });
        return response.data;
    } catch (err) {
        console.error('Error in clearMemory:', err);
        throw err;
    }
};
